"use client";

import Contact from "@/components/Contact/Contact.component";
import Footer from "@/components/Footer/Footer.component";
import GlobalNav from "@/components/GlobalNav/GlobalNav.component";
import Logo from "@/components/Logo/Logo.component";
import StickyLozenge from "@/components/StickyLozenge/StickyLozenge.component";
import { ShowContactActionKind } from "@/context/PageContext/PageContext.model";
import { usePageContext } from "@/context/PageContext/PageContextProvider";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { RxHamburgerMenu } from "react-icons/rx";
import { CSSTransition } from "react-transition-group";
import { LayoutProps } from "./Layout.model";

export const contentRef = React.createRef<HTMLDivElement>();

export default function Layout({ className, data, children }: LayoutProps) {
  const pathname = usePathname();
  const { dispatch } = usePageContext();
  if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin, useGSAP);
  }
  const state = usePageContext();

  const layoutRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const contactTransitionRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoints();

  const {
    menuItems,
    page: {
      template: { contact, footer },
    },
  } = data;

  const [showContact, setShowContact] = useState<boolean>(false);
  const [showLozenge, setShowLozenge] = useState<boolean>(true);

  const isFooterFullHeight = () => {
    if (typeof window !== "undefined") {
      const { innerHeight: height } = window;
      const footerHeight = footerRef.current?.clientHeight || 0;
      return footerHeight > height;
    }
    return false;
  };

  const revealContactModule = () => {
    dispatch({
      type: ShowContactActionKind.SET_CONTACT_STATUS,
      payload: { showContact: true },
    });
  };

  const closeContactModule = () => {
    dispatch({
      type: ShowContactActionKind.SET_CONTACT_STATUS,
      payload: { showContact: false },
    });
    setShowContact(false);
  };

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`(min-width: 768px)`, () => {
        ScrollSmoother.create({
          smooth: 1,
          effects: false,
          smoothTouch: 0.1,
          normalizeScroll: {
            allowNestedScroll: true,
          },
        });
      });
    },
    {
      scope: layoutRef,
      dependencies: [pathname],
      revertOnUpdate: true,
    },
  );

  useGSAP(
    () => {
      const endVlaue =
        isMobile || isFooterFullHeight()
          ? `+=h-screen`
          : `+=${footerRef.current?.clientHeight}`;
      ScrollTrigger.create({
        trigger: footerRef.current,
        pin: true,
        scrub: true,
        start: isMobile ? "top bottom" : "bottom bottom",
        end: endVlaue,
        onEnter: () => {
          ScrollTrigger.normalizeScroll({ allowNestedScroll: true });
          setShowLozenge(false);
        },
        onLeaveBack: () => {
          ScrollTrigger.normalizeScroll(false);
          setShowLozenge(true);
          closeContactModule();
        },
      });
    },
    { dependencies: [isMobile], revertOnUpdate: true, scope: layoutRef },
  );

  useGSAP(
    () => {
      const preventScroll = ScrollTrigger.observe({
        preventDefault: false,
        target: layoutRef.current,
        type: "wheel,scroll,touch,pointer",
        allowClicks: false,
        onEnable: self => ((self as any).savedScroll = self.scrollY()),
        onChangeY: self => self.scrollY((self as any).savedScroll),
      });
      preventScroll.disable();

      if (showContact && footerRef.current) {
        gsap
          .to(window, {
            duration: 0.6,
            scrollTo: {
              y: footerRef.current,
              offsetY: -footerRef.current?.clientHeight,
            },
          })
          .then(() => preventScroll.enable());
      } else {
        preventScroll.disable();
      }
    },
    { dependencies: [showContact], revertOnUpdate: false },
  );

  useEffect(() => {
    setShowContact(state.state.showContact);
  }, [state]);

  return (
    <>
      {menuItems && (
        <GlobalNav
          items={menuItems}
          title="MENU"
          logo="/static/img/chepnetwork_logo.svg"
          icon={<RxHamburgerMenu />}
          handleContactClick={revealContactModule}
        />
      )}
      {showLozenge && (
        <StickyLozenge
          icon="/static/icons/smiley.svg"
          buttonText="get in touch"
          handleClick={revealContactModule}
        />
      )}
      <div id="smooth-wrapper" className="layout" ref={layoutRef}>
        <div id="smooth-content" className="relative will-change-transform">
          <Logo theme={data.page.template.logoTheme} />
          <div
            className={cn("layout__content bg-core-white", className)}
            ref={contentRef}
          >
            {children}
          </div>
          {footer && (
            <div
              ref={footerRef}
              className={cn(
                "bg-core-black relative z-[-1] box-border w-full p-0 will-change-transform",
                isMobile || isFooterFullHeight()
                  ? "mt-[-h-screen] h-screen"
                  : "mt-[-472px] h-[472px]",
              )}
            >
              <Footer
                {...footer}
                buttonText={contact.buttonText}
                setShowContact={setShowContact}
              />
            </div>
          )}

          {/* Tablet & Desktop */}
          {!isMobile && contact && (
            <CSSTransition
              in={showContact}
              nodeRef={contactTransitionRef}
              timeout={1000}
              unmountOnExit
              classNames={{
                enter:
                  "transition-translate duration-1000 animate-bounce-in-up max-md:animate-slide-up",
                exit: "transition-translate duration-1000 animate-slide-down",
              }}
            >
              <div
                ref={contactTransitionRef}
                className="bottom-lg right-lg absolute z-[99] max-md:fixed max-md:top-auto max-md:right-0 max-md:bottom-0 max-md:h-screen"
              >
                <Contact {...contact} setShowContact={setShowContact} />
              </div>
            </CSSTransition>
          )}

          {/* Mobile */}
          {isMobile && contact && showContact && (
            <div
              ref={contactRef}
              className="bottom-lg right-lg absolute z-[99] max-md:fixed max-md:top-auto max-md:right-0 max-md:bottom-0 max-md:h-screen"
            >
              <Contact {...contact} setShowContact={setShowContact} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
